import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import { get } from 'lodash';
import dateFormat from 'dateformat';
import { toast } from 'react-toastify';
import { Calendar } from 'primereact/calendar';
import { addLocale } from 'primereact/api';
import { Input200px } from '../../../styles/inputGroup';
import Card from '../../../components/card';
import Loading from '../../loading';
import { formataMoeda, displayErrors } from '../../../util/diversos';

import store from '../../../store/store';
import axiosAdmin from '../../../services/axiosAdmin';

export default function RelatorioEstampariaPedidoFinanceiro() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [isLoading, setIsLoading] = useState(false);
  const [findDataInicial, setFindDataInicial] = useState(null);
  const [findDataFinal, setFindDataFinal] = useState(null);

  addLocale('pt-BR', {
    firstDayOfWeek: 0,
    dayNames: ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'],
    dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
    dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
    monthNames: ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'],
    monthNamesShort: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
    today: 'hoje',
    clear: 'Limpar',
  });

  function headRows() {
    return [
      { data: 'Data', entrada: 'Entrada', meioPagamento: 'Meio Pagamento', numeroParcelas: 'Parcelas', valor: 'Valor' },
    ];
  }

  const [headerTabelaImpressao] = useState(headRows());

  const idUsuario = get(store.getState().auth.user, 'id', 0);

  async function getData(searchFilter, img) {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    try {
      setIsLoading(true);

      if (findDataInicial == null && findDataFinal == null) {
        toast.info('Datas devem ser informadas.');
        return;
      }

      if (findDataInicial > findDataFinal) {
        toast.info('Data final anterior a data inicial.');
        return;
      }

      let params = `/estampariapedido/search-pedido-pagamento?idUsuario=${idUsuario}`;
      if (searchFilter && searchFilter.findDataInicial !== '') {
        const _dataInicial = dateFormat(searchFilter.findDataInicial, 'dd-mm-yyyy 00:00:00');
        const _dataFinal = dateFormat(searchFilter.findDataFinal, 'dd-mm-yyyy 23:59:59');

        params = `${params}&dataInicial=${_dataInicial}&dataFinal=${_dataFinal}`;

        params = `${params}&page=0&size=9999`;
      }

      const response = await axiosAdmin.get(params);

      if (response.data.content <= 0) {
        toast.info('Não há registros a serem exibidos.');
        return;
      }

      let data = {
        data: '',
        entrada: '',
        meioPagamento: '',
        numeroParcelas: '',
        valor: '',
      };

      const result = [];
      let valorTotalPedidos = 0;
      for (let x = 0; x < response.data.content.length; x++) {
        const pedidoFinanceiro = response.data.content[x];

        data.data = `${('00' + pedidoFinanceiro.dataPagamento[2]).slice(-2)}/${('00' + pedidoFinanceiro.dataPagamento[1]).slice(-2)}/${pedidoFinanceiro.dataPagamento[0]}`;
        data.entrada = pedidoFinanceiro.entrada ? 'Sim' : 'Não';
        data.meioPagamento = pedidoFinanceiro.meioPagamento.descricao;
        data.numeroParcelas = pedidoFinanceiro.numeroParcelas;
        data.valor = formataMoeda(pedidoFinanceiro.valor);

        valorTotalPedidos = valorTotalPedidos + pedidoFinanceiro.valor;
        
        result.push({ ...data });
      }

      // adionar linha vazia
      data = {
        data: '',
        entrada: '',
        meioPagamento: '',
        numeroParcelas: 'Total ',
        valor: formataMoeda(valorTotalPedidos).toString(),
      };
      result.push({ ...data });

      const resultado = Object.values(response.data.content.reduce((hash, item) => {
        if (!hash[item.meioPagamento.descricao]) {
          hash[item.meioPagamento.descricao] = { name: item.meioPagamento.descricao, amount: 0 };
        }
        hash[item.meioPagamento.descricao].amount += item.valor;
        return hash;
      }, {}));

      // adionar linha vazia
      data = {
        data: '',
        entrada: '',
        meioPagamento: '',
        numeroParcelas: '',
        valor: '',
      };
      result.push({ ...data });

      resultado.forEach(element => { 
        data = {
          data: '',
          entrada: '',
          meioPagamento: element.name,
          numeroParcelas: '',
          valor: formataMoeda(element.amount).toString(),
        };

        result.push({ ...data });
      });

      doc.autoTable({
        styles: { fontSize: 8 },
        margin: { top: 38, left: 5, right: 5 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 23 },
          3: { cellWidth: 20 },
          4: { cellWidth: 30 },
        },
        theme: 'grid',
        head: headerTabelaImpressao,
        body: result,
        didDrawPage: dados => {
          // Header
          doc.rect(5, 5, 200, 30);
          doc.addImage(img, 'JPEG', 9, 8, 25, 25);
          doc.setFontSize(20);
          doc.text('Relatório de Pedidos - Financeiro ', 60, 15);

          doc.setFontSize(8);
          doc.text('Período:', 50, 32);
          doc.text(dateFormat(findDataInicial, 'dd/mm/yyyy'), 63, 32);
          doc.text(' à ', 79, 32);
          doc.text(dateFormat(findDataFinal, 'dd/mm/yyyy'), 83, 32);

          // footer
          const { pageSize } = doc.internal;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          doc.text(`Pag ${doc.internal.getNumberOfPages()}`, dados.settings.margin.right, pageHeight - 8);
          const periodo = new Date();
          doc.text('Impresso: ', 264, pageHeight - 8);
          // eslint-disable-next-line prefer-template
          doc.text((`${('00' + periodo.getDate()).slice(-2)}/${('00' + (+periodo.getMonth() + 1)).slice(-2)}/${periodo.getFullYear()}`).toString(), 278, pageHeight - 8);
        },
      });
      window.open(doc.output('bloburl'));
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  const handleFind = () => {
    const searchFilter = { findDataInicial, findDataFinal };

    const loadImage = async () => {
      let config = '';
      try {
        config = await axiosAdmin.get(`/configuracaosistema?idUsuario=${idUsuario}`);
      } catch (err) {
        displayErrors(err, dispatch, navigate, '/', '');
      }

      const img = new Image();
      img.src = `/logotipos/${config.data.urlLogotipo}`;
      await img.decode().then(() => {

      }).catch(() => {
        img.src = 'logo.png';
      });
      getData(searchFilter, img);
    };
    loadImage();
  };

  return (
    <>
      <Loading isLoading={isLoading} />

      <Card>
        <div className="card mb-3">
          <h6 className="card-header" style={{ textAlign: 'center' }}> Relatório de Pedidos - Financeiro </h6>
        </div>

        <div className="row align-items-center">
          <div className="col-lg-4">
            <div className="p-inputgroup">
              <Input200px className="p-inputgroup-addon">
                Data inicial
              </Input200px>
              <Calendar
                id="mask"
                showIcon
                hourFormat="24"
                mask="99/99/9999 99:99:99"
                dateFormat="dd/mm/yy"
                showButtonBar
                showOnFocus={false}
                locale="pt-BR"
                value={findDataInicial}
                onChange={(e) => setFindDataInicial(e.value)}
              />
            </div>
          </div>
          <div className="col-lg-4">
            <div className="p-inputgroup">
              <Input200px className="p-inputgroup-addon">
                Data final
              </Input200px>
              <Calendar
                id="mask"
                hourFormat="24"
                showIcon
                mask="99/99/9999 99:99:99"
                dateFormat="dd/mm/yy"
                showButtonBar
                showOnFocus={false}
                locale="pt-BR"
                value={findDataFinal}
                onChange={(e) => setFindDataFinal(e.value)}
              />
            </div>
          </div>

          <div className="col-lg-3">
            <button
              type="button"
              title="Buscar"
              className="btn btn-success"
              style={{ borderRadius: '15px' }}
              onClick={handleFind}
            >
              <i className="pi pi-search"></i>
            </button>
          </div>
        </div>
      </Card>
    </>
  );
}
